import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Routes, Route, useNavigate } from 'react-router-dom';
import { LazyMotion, domAnimation } from 'framer-motion';
import { ThemeProvider } from '../../context/ThemeContext';
import { LanguageProvider } from '../../context/LanguageContext';
import { CurrencyProvider } from '../../context/CurrencyContext';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';
import { STRIPE_CONFIG } from '../../config/stripe';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';
import BookingOverview from './BookingOverview';
import ExtendFlow from './ExtendFlow';
import CancelFlow from './CancelFlow';
import EditFlow from './EditFlow';
import PayFlow from './PayFlow';

const API_BASE = STRIPE_CONFIG.apiBaseUrl || '/api';

function BookingManagementInner({ token, bookingData, refreshBooking }) {
  const merchantData = bookingData.merchant
    ? { config: bookingData.merchant }
    : null;

  useDocumentMeta(merchantData);

  return (
    <LazyMotion features={domAnimation}>
      <ThemeProvider merchantData={merchantData}>
        <LanguageProvider merchantData={bookingData.merchant}>
          <CurrencyProvider merchantData={bookingData.merchant}>
            <div className="min-h-screen bg-gray-50">
              <Routes>
                <Route
                  index
                  element={
                    <BookingOverview
                      token={token}
                      bookingData={bookingData}
                      refreshBooking={refreshBooking}
                    />
                  }
                />
                <Route
                  path="extend"
                  element={
                    <ExtendFlow
                      token={token}
                      bookingData={bookingData}
                      refreshBooking={refreshBooking}
                    />
                  }
                />
                <Route
                  path="pay"
                  element={
                    <PayFlow
                      token={token}
                      bookingData={bookingData}
                      refreshBooking={refreshBooking}
                    />
                  }
                />
                <Route
                  path="cancel"
                  element={
                    <CancelFlow
                      token={token}
                      bookingData={bookingData}
                      refreshBooking={refreshBooking}
                    />
                  }
                />
                <Route
                  path="edit/*"
                  element={
                    <EditFlow
                      token={token}
                      bookingData={bookingData}
                      refreshBooking={refreshBooking}
                    />
                  }
                />
              </Routes>
            </div>
          </CurrencyProvider>
        </LanguageProvider>
      </ThemeProvider>
    </LazyMotion>
  );
}

export default function BookingManagement() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBooking = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const validateRes = await fetch(`${API_BASE}/manage/validate-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      if (!validateRes.ok) {
        const err = await validateRes.json().catch(() => ({}));
        throw new Error(err.error || 'Invalid or expired link');
      }

      const bookingRes = await fetch(`${API_BASE}/manage/booking`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!bookingRes.ok) {
        const err = await bookingRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to load booking');
      }

      const data = await bookingRes.json();
      setBookingData(data);
    } catch (err) {
      console.error('[BOOKING-MANAGEMENT] Error:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  const refreshBooking = useCallback(async () => {
    try {
      const bookingRes = await fetch(`${API_BASE}/manage/booking`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!bookingRes.ok) return;
      const data = await bookingRes.json();
      setBookingData(data);
    } catch {
      // silent refresh — don't show error
    }
  }, [token]);

  if (loading) {
    return <LoadingState />;
  }

  if (error || !bookingData) {
    return <ErrorState message={error || 'Booking not found'} />;
  }

  return (
    <BookingManagementInner
      token={token}
      bookingData={bookingData}
      refreshBooking={refreshBooking}
    />
  );
}
